import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import apiUrl from '../../Apiurl';
import stilePulsanti from '../../interfaccia/stilePulsanti';
import SettingBooleano from '../../statistiche/SettingBooleano'; // Still used for basic toggles? OR custom? Custom for colors.
import { LineChart } from "@mui/x-charts/LineChart";
import { LineChartSkeleton } from "../../interfaccia/loading";

function StatTrendFacet({
    facetKey, // "reparti", "categorie", "tipi"
    title,
    mobile,
    token,
    lookupMap // Map<code, description>
}) {
    const {
        buttonClassesWhite,
        buttonClassesGreen,
        buttonClassesOrange,
        inputClassesRicercaOrizzontale
    } = stilePulsanti;

    const [years, setYears] = useState(3);
    const [metric, setMetric] = useState("importo"); // "importo" | "quantita"
    const [stack, setStack] = useState(false);
    
    // Data state
    const [chartData, setChartData] = useState([]);
    const [seriesList, setSeriesList] = useState([]); // [{key, label?}]
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Visual state
    const [visibleSeries, setVisibleSeries] = useState({}); // { key: boolean }
    const [seriesColors, setSeriesColors] = useState({});

    // Palette
    const PALETTE = [
        "#ff9800", "#009688", "#1565c0", "#9c27b0", "#b71c1c",
        "#e91e63", "#8bc34a", "#00bcd4", "#795548", "#607d8b",
        "#ffeb3b", "#3f51b5", "#f44336", "#9e9e9e", "#4caf50",
        "#ffc107", "#03a9f4", "#e91e63", "#673ab7", "#cddc39"
    ];

    useEffect(() => {
        const fetchData = async () => {
            if (!token) return;
            setLoading(true);
            setError("");
            try {
                const params = new URLSearchParams({
                    facet: facetKey,
                    anni: String(years),
                    metric: metric
                });
                
                const res = await axios.get(
                    `${apiUrl}/logistica/dashboard/trendArticoliFacet?${params.toString()}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                const data = res.data;
                if (!data) throw new Error("No data");

                // Process Dataset
                // Provide robust sort and parsing
                const rawDataset = Array.isArray(data.dataset) ? data.dataset : [];
                const incomingSeries = Array.isArray(data.series) ? data.series : []; // [{key: '...'}]

                const processedDataset = rawDataset
                    .map(row => {
                        if (!row?.month) return null;
                        const date = new Date(`${row.month}-01T00:00:00`);
                        if (isNaN(date.getTime())) return null;
                        
                        const newRow = { month: date };
                        incomingSeries.forEach(s => {
                            const val = Number(row[s.key]);
                            newRow[s.key] = isFinite(val) ? val : 0; 
                        });
                        return newRow;
                    })
                    .filter(Boolean)
                    .sort((a, b) => a.month - b.month);

                setChartData(processedDataset);
                setSeriesList(incomingSeries);

                // Assign Colors (stable logic)
                const colors = {};
                incomingSeries.forEach((s, idx) => {
                    colors[s.key] = PALETTE[idx % PALETTE.length];
                });
                setSeriesColors(colors);

                // Default Visibility: All enabled initially? 
                // Or preserve if keys match? 
                // Let's reset for simplicity on content change
                const vis = {};
                incomingSeries.forEach(s => vis[s.key] = true);
                setVisibleSeries(vis);

            } catch (err) {
                console.error(`Error fetching trend facet ${facetKey}`, err);
                setError("Errore caricamento dati");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [facetKey, years, metric, token]);


    const renderControls = () => (
        <div className="flex flex-wrap items-center justify-between mb2">
            <div className="mr3 flex items-center mb2">
                <span className="mr2">ultimi</span>
                <input
                    type="number"
                    min={1}
                    max={10}
                    value={years}
                    onChange={(e) => {
                        const v = Number(e.target.value);
                        setYears(Math.min(10, Math.max(1, v)));
                    }}
                    className={inputClassesRicercaOrizzontale + " w3 tc"}
                />
                <span className="ml2">anni</span>
            </div>

            <div className="mr3 flex items-center mb2">
                <span className="mr2">importo/quantità</span>
                <SettingBooleano
                    value={metric}
                    trueButtonClass={buttonClassesOrange}
                    falseButtonClass={buttonClassesGreen}
                    trueElement={"importo"}
                    falseElement={"quantità"}
                    setTrue={() => setMetric("importo")}
                    setFalse={() => setMetric("quantita")}
                    customValueTrue={"importo"}
                    customValueFalse={"quantita"}
                />
            </div>

            <div className="mr3 flex items-center mb2">
                <span className="mr2">stack/linee</span>
                <SettingBooleano
                    value={stack}
                    trueButtonClass={buttonClassesOrange}
                    falseButtonClass={buttonClassesGreen}
                    trueElement={"stack"}
                    falseElement={"linee"}
                    setTrue={() => setStack(true)}
                    setFalse={() => setStack(false)}
                />
            </div>
        </div>
    );

    const getLabel = (key) => {
        if (!key || key.trim() === "") return "N/D";
        if (lookupMap && lookupMap instanceof Map && lookupMap.get(key)) {
            return `${key} - ${lookupMap.get(key)}`;
        }
        return key;
    };

    const renderToggles = () => (
        <div className="flex flex-wrap mb2 justify-center">
            {seriesList.map(s => {
                const isActive = visibleSeries[s.key];
                const color = seriesColors[s.key] || "#000";
                return (
                    <button
                        key={s.key}
                        onClick={() => setVisibleSeries(prev => ({ ...prev, [s.key]: !prev[s.key] }))}
                        className="ma1 pa2 pointer br2 b--none white f7"
                        style={{
                            backgroundColor: isActive ? color : '#eee',
                            color: isActive ? '#fff' : '#333',
                            border: isActive ? 'none' : '1px solid #ccc'
                        }}
                    >
                        {getLabel(s.key)}
                    </button>
                )
            })}
        </div>
    );

    const renderChart = () => {
        if (loading) {
            return (
                <LineChartSkeleton
                    tone="soft"
                    height={mobile ? 300 : 700}
                    seriesCount={Math.max(2, Math.min(seriesList.length || 3, 4))}
                />
            );
        }
        if (error) return <div className="dark-red tc pa3">{error}</div>;
        if (chartData.length === 0) return <div className="gray tc pa3">Nessun dato</div>;
        
        // Prepare Series for MUI
        const activeKeys = seriesList.filter(s => visibleSeries[s.key]).map(s => s.key);
        
        if (activeKeys.length === 0) return <div className="gray tc pa3">Seleziona almeno una serie</div>;

        const valueFormatter = (v) => {
            if (metric === "importo") return `${Number(v).toLocaleString('it-IT', { minimumFractionDigits: 2 })} €`;
            return v;
        }

        const series = activeKeys.map(key => ({
            dataKey: key,
            label: getLabel(key),
            valueFormatter,
            showMark: true,
            strokeWidth: 2,
            color: seriesColors[key],
            ...(stack ? { stack: "total", area: true, stackOffset: "none" } : {}),
            data: chartData.map(d => d[key]) 
        }));

        return (
            <div style={{ width: '100%', height: mobile ? 300 : 700 }}>
                 <LineChart
                    dataset={chartData}
                    xAxis={[{
                        dataKey: 'month',
                        scaleType: 'time',
                        valueFormatter: (v) => v ? new Date(v).toLocaleDateString("it-IT", { month: '2-digit', year: '2-digit' }) : ""
                    }]}
                    series={series}
                    height={mobile ? 300 : 700}
                    margin={{ left: 70, right: 40, top: 20, bottom: 50 }}
                 />
            </div>
        )
    };

    return (
        <div className="sfondoBiancoTema pa3 mb3">
            <div className="tc b mb2 f4">{title} ({metric})</div>
            {renderControls()}
            {renderToggles()}
            {renderChart()}
        </div>
    );
}

export default StatTrendFacet;
